import React, { useEffect, useState } from 'react';
import { InternalPageRoot, PdfOrImgViewer } from "../../../index";
import { useTheme } from "../../../../common/ProjectThemeProvider";
import { checkNullStr, getCurrUrlMainDomain, getCurrUrlParamValue, getJsonValue, isBoolTrue, isJsonValueTrue } from "../../../../common/fusion-ui/utils/ReactUtils";
import { formatDateValue, getAllocatedModuleName, printError, printLog, sendRequest } from "../../../utils/AppUtils";
import { useToast } from "../../../../common/fusion-ui/provider/ToastProvider";
import { CssVariant, DefStrNA, Messages } from 'common/fusion-ui/config/FusionConfig';
import { GenRouteUrls, Keys, ModuleFieldsData, Modules, ReqUrls, ShareFeatureModules, ShowViewType } from "../../../config/AppConfig";
import { useConfig } from "../../../context/ConfigProvider";

const Meeting_InternalPage = React.memo(({
    open,
    onHide,
    recordId,
    showAs = ShowViewType.PAGE,
}) => {
    const TAG = "Meeting_InternalPage";

    const theme = useTheme();
    const { showToast, changeToastVariant } = useToast();
    const { isConfigTrue, updateConfig } = useConfig();

    const moduleName = Modules.Meetings;
    const mf = ModuleFieldsData[moduleName];

    const Fields = {
        recordId: 'recordId',
        pageDataObject: 'pageDataObject',

        isDataLoading: 'isDataLoading',
        isDataFound: 'isDataFound',
        dataLoadingMsg: 'dataLoadingMsg',
        isDataFetched: 'isDataFetched',

        prevRecordId: 'prevRecordId',
        nextRecordId: 'nextRecordId',
    };
    const [fieldsData, setFieldsData] = useState({});


    useEffect(() => {
        // log('useEffect.showAs:', showAs, 'recordId:', recordId);
        if (showAs == ShowViewType.PAGE) {
            const id = getCurrUrlParamValue('id');
            fetchPageData(id, true);
        } else {
            if (checkNullStr(recordId)) {
                fetchPageData(recordId, false);
            }
        }
    }, [recordId, showAs]);

    return (<>
        <InternalPageRoot
            tag={TAG}
            moduleName={moduleName}
            title={`${getAllocatedModuleName(moduleName)} Details`}
            backToListText={`Back to ${getAllocatedModuleName(moduleName).toLowerCase()}`}
            isDataFound={getField(Fields.isDataFound)}
            isDataLoading={getField(Fields.isDataLoading)}
            dataLoadingMsg={getField(Fields.dataLoadingMsg)}
            internalPageUrl={GenRouteUrls.meeting_internalPage}
            prevRecordId={getField(Fields.prevRecordId)}
            nextRecordId={getField(Fields.nextRecordId)}
            onPrev={() => fetchPageData(getField(Fields.prevRecordId), false)}
            onNext={() => fetchPageData(getField(Fields.nextRecordId), false)}
            showShare={ShareFeatureModules.some((item) => item.tag == TAG)}
            shareUrl={`${getCurrUrlMainDomain()}${GenRouteUrls.meeting_internalPage}${getField(Fields.recordId)}`}
            open={open}
            onHide={onHide}
            showAs={showAs}>
            <div>
                <div className={`m-0 p-0 px-1x w-100x`}>
                    <table className={'w-100x'} style={{ color: theme.text }}>
                        <tbody>
                            {/* Updated by Vandit Tewari on (6/11/24) - in every tr given a class 'wmInternalPageTrBreak' done this bcz below 560px the text in some td getting out of screen as these both comes in same row - declared in fusion css also in all td added class text-break*/}
                            <tr className='wmInternalPageTrBreak'>
                                <th className={'text-nowrap flex-nowrap d-flex al-start jc-start'}>{mf.title.titleAlt}:</th>
                                <td className={'px-2 text-wrap text-break'}>{getPageField(mf.title, DefStrNA)}</td>
                            </tr>

                            {/* Outline */}
                            <tr className='wmInternalPageTrBreak'>
                                <th className={'text-nowrap flex-nowrap d-flex al-start jc-start'}>{mf.outline.title}:</th>
                                <td className={'px-2 text-wrap text-break'}>
                                    <p className={'text-wrap text-break text-block text-start w-100 m-0 p-0'}>
                                        {getPageField(mf.outline, DefStrNA)}
                                    </p>
                                </td>
                            </tr>

                            {/* desc */}
                            <tr className='wmInternalPageTrBreak'>
                                <th className={'text-nowrap flex-nowrap d-flex al-start jc-start'}>{mf.desc.title}:</th>
                                <td className={'px-2 text-break'}>
                                    <div className={'text-break text-wrap'}
                                        dangerouslySetInnerHTML={{ __html: getPageField(mf.desc, DefStrNA) }} />
                                </td>
                            </tr>

                            {/* date */}
                            <tr className='wmInternalPageTrBreak'>
                                <th className={'text-nowrap flex-nowrap d-flex al-start jc-start'}>{mf.date.titleAlt}:</th>
                                <td className={'px-2 text-wrap text-break'}>{formatDateValue(getPageField(mf.date, DefStrNA))}</td>
                            </tr>

                            {/* venue */}
                            <tr className='wmInternalPageTrBreak'>
                                <th className={'text-nowrap flex-nowrap d-flex al-start jc-start'}>{mf.venue.titleAlt}:</th>
                                <td className={'px-2 text-wrap text-break'}>{getPageField(mf.venue, DefStrNA)}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                {/* Document viewer */}
                <div className={'my-3'}>
                    {/*pdf*/}
                    <PdfOrImgViewer
                        src={getPageField(mf.doc)}
                        fileExt={getPageField(mf.docFileExt)}
                    />
                </div>
            </div>
        </InternalPageRoot>
    </>)


    //======================Request Functions start=====================
    async function fetchPageData(recordId, checkBefore = true) {
        const fun = 'fetchPageData:';
        if (!checkBefore || (checkBefore && !isFieldTrue(Fields.isDataFetched))) {
            log(fun, 'recordId:', recordId);
            updateField(Fields.isDataFetched, true);
            updateField(Fields.recordId, recordId);
            if (checkNullStr(recordId)) {
                updateField(Fields.pageDataObject, {});
                updateField(Fields.isDataFound, false);
                updateField(Fields.isDataLoading, true);
                updateField(Fields.dataLoadingMsg, "");
                updateField(Fields.nextRecordId, null);
                updateField(Fields.prevRecordId, null);

                const REQUEST_OPTIONS = {
                    recordId: recordId,
                    getAdjacentRecordIds: true,
                };

                try {
                    sendRequest({
                        reqUrl: ReqUrls.meeting__addEditPage_fetch_editFormData,
                        reqOptions: REQUEST_OPTIONS,
                        onFetched: ((fetchedData) => {
                            log(fun, 'fetchedData:', fetchedData);

                            const fetchedPageData = getJsonValue(fetchedData, 'data', {});
                            const success = isJsonValueTrue(fetchedData, 'success');
                            const message = getJsonValue(fetchedData, 'message');

                            if (success) {
                                // toast(message, CssVariant.success)
                                updateField(Fields.pageDataObject, fetchedPageData);
                                updateConfig(Keys.sharePanel_recordTitle, getJsonValue(fetchedPageData, mf.title.column));
                                updateConfig(Keys.sharePanel_recordDescription, getJsonValue(fetchedPageData, mf.outline.column));

                                updateField(Fields.nextRecordId, getJsonValue(fetchedPageData, 'nextRecordId'));
                                updateField(Fields.prevRecordId, getJsonValue(fetchedPageData, 'prevRecordId'));
                            } else {
                                toast(message, CssVariant.danger)
                                updateField(Fields.dataLoadingMsg, message);
                            }
                            updateField(Fields.isDataFound, success);
                            updateField(Fields.isDataLoading, false);
                        }),
                        onError: ((error) => {
                            logErr(fun, error);
                            toast(Messages.fetchError, CssVariant.danger)
                            updateField(Fields.dataLoadingMsg, Messages.fetchError);
                            updateField(Fields.isDataFound, false);
                            updateField(Fields.isDataLoading, false);
                        }),
                    });
                } catch (error) {
                    toast(Messages.fetchError, CssVariant.danger)
                    logErr(fun, error);
                    updateField(Fields.dataLoadingMsg, Messages.fetchError);
                    updateField(Fields.isDataFound, false);
                    updateField(Fields.isDataLoading, false);
                }
            } else {
                toast('Record-id not found!', CssVariant.danger);
                updateField(Fields.dataLoadingMsg, 'Record-id not found!');
                updateField(Fields.isDataFound, false);
                updateField(Fields.isDataLoading, false);
            }
        }
    }

    //======================Request Functions end=====================

    //======================Util Functions start=====================

    function getPageField(colField, defValue) {
        return getJsonValue(getField(Fields.pageDataObject), colField.column, defValue);
    }

    function updateField(field, value) {
        setFieldsData(prevState => ({ ...prevState, [field]: value }));
    }

    function getField(field, defValue) {
        return getJsonValue(fieldsData, field, defValue);
    }

    function isFieldTrue(field) {
        return isBoolTrue(getField(field), true, true);
    }

    function toast(text, variant) {
        showToast(text);
        changeToastVariant(variant);
    }

    function log(...text) {
        printLog(TAG, ...text);
    }

    function logErr(...text) {
        printError(TAG, ...text);
    }

    //======================Util Functions end=====================
});
export default Meeting_InternalPage;
